import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { api } from '../../context/AuthContext';
import { INTERNAL_KYC_UI } from '../../lib/featureFlags';
import { cn } from '../../lib/utils';
import { 
  Users,
  Buildings,
  TrendUp,
  Warning,
  Wallet,
  ChartBar,
  Clock,
  CaretRight,
  UsersFour,
  Kanban,
  Calculator,
  ChartPie,
  Sparkle,
  ArrowsClockwise,
  Coins,
  FileText,
  Eye,
  PencilSimple,
  Monitor,
  DeviceMobile,
  House,
  Receipt,
  Ticket
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import InfoTooltip from '../../components/InfoTooltip';

/**
 * KPI Card Component - Premium white theme
 */
const KPICard = ({ 
  value, 
  label, 
  description, 
  icon: Icon, 
  iconColor = 'text-primary',
  bgColor = 'bg-primary/10',
  format = 'number',
  onClick,
  tooltip
}) => {
  const formatValue = (val) => {
    if (format === 'currency') {
      return `€${(val || 0).toLocaleString()}`;
    }
    return (val || 0).toLocaleString();
  };

  return (
    <Card 
      className={cn(
        'bg-white rounded-2xl border border-black/[0.04] p-4 md:p-5 cursor-pointer transition-all duration-300',
        'hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5'
      )}
      onClick={onClick}
      data-testid={`kpi-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Header row: Icon + Label */}
      <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
        <div className={cn('p-2 md:p-2.5 rounded-xl', bgColor)}>
          <Icon className={cn('h-4 w-4 md:h-5 md:w-5', iconColor)} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-xs md:text-sm font-medium text-foreground-muted truncate block">{label}</span>
          {tooltip && <InfoTooltip content={tooltip} iconSize={12} />}
        </div>
        <CaretRight className="h-4 w-4 text-foreground-muted/50 hidden md:block" />
      </div>
      {/* Value */}
      <div className="text-xl md:text-3xl font-heading font-bold mb-1">{formatValue(value)}</div>
      {/* Description - hidden on mobile */}
      <div className="text-xs text-foreground-muted hidden md:block">{description}</div>
    </Card>
  );
};

/**
 * Admin Tool Card - Desktop vs Mobile
 */
const AdminToolCard = ({ 
  title, 
  subtitle, 
  description, 
  icon: Icon, 
  iconColor, 
  bgColor, 
  onClick,
  badge,
  testId,
  editOnly = false
}) => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <Card 
      className={cn(
        "bg-white rounded-2xl border border-black/[0.04] p-4 md:p-5 cursor-pointer transition-all",
        "hover:border-primary/20 hover:shadow-lg",
        editOnly && isMobile && "opacity-60"
      )}
      onClick={!editOnly || !isMobile ? onClick : undefined}
      data-testid={testId}
    >
      <div className="flex items-center gap-3 mb-2 md:mb-3">
        <div className={cn('p-2 md:p-2.5 rounded-xl', bgColor)}>
          <Icon className={cn('h-4 w-4 md:h-5 md:w-5', iconColor)} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm md:text-base truncate">{title}</h3>
            {badge && (
              <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-primary/10 text-primary rounded-full flex items-center gap-0.5">
                <Sparkle className="h-2.5 w-2.5" weight="fill" />
                {badge}
              </span>
            )}
          </div>
          <p className="text-xs text-foreground-muted hidden md:block">{subtitle}</p>
        </div>
        {/* Desktop: show arrow, Mobile: show view-only indicator if editOnly */}
        {isMobile && editOnly ? (
          <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-600 flex items-center gap-1">
            <Monitor className="h-3 w-3" />
            Desktop
          </Badge>
        ) : (
          <CaretRight className="h-4 w-4 text-foreground-muted/50" />
        )}
      </div>
      {/* Description - hidden on mobile */}
      <p className="text-sm text-foreground-muted hidden md:block">{description}</p>
      {/* Mobile view-only message */}
      {isMobile && editOnly && (
        <p className="text-xs text-amber-600 mt-1">Open on desktop to edit</p>
      )}
    </Card>
  );
};

/**
 * Admin Dashboard Page - Premium White Theme with Mobile Support
 */
const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingSubscriptions, setPendingSubscriptions] = useState(0);
  
  // Time period filter
  const [period, setPeriod] = useState(searchParams.get('period') || 'all');
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [kpisRes, subsRes] = await Promise.all([
        api.get(`/admin/dashboard/kpis?period=${period}`),
        api.get('/admin/v2/subscriptions/stats/summary').catch(() => ({ data: { pending: 0 } }))
      ]);
      setKpis(kpisRes.data.kpis);
      setPendingSubscriptions(subsRes.data.pending || 0);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Handle period change
  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    setSearchParams(newPeriod !== 'all' ? { period: newPeriod } : {}, { replace: true });
  };

  // Handle KPI drill-down
  const handleKPIDrillDown = (drillDown) => {
    if (drillDown) {
      navigate(drillDown);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[rgb(var(--background))]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[rgb(var(--background))]" data-testid="admin-dashboard-page">

      <main className="py-6 md:py-8">
        <div className="container mx-auto px-4 md:px-6 lg:px-12 xl:px-20">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <p className="text-primary font-medium tracking-[0.2em] uppercase text-xs md:text-sm mb-1 md:mb-2">
                Administration
              </p>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-medium">Dashboard</h1>
              <p className="text-sm md:text-base text-foreground-muted mt-1">
                {isMobile ? 'View-only on mobile' : 'Platform overview and management'}
              </p>
            </div>
            
            {/* Time Period Filter */}
            <div className="flex items-center gap-2 md:gap-3">
              <span className="text-xs md:text-sm text-foreground-muted hidden md:block">Period:</span>
              <Select value={period} onValueChange={handlePeriodChange}>
                <SelectTrigger className="w-[120px] md:w-[140px] bg-white border-black/[0.08] rounded-full text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-black/[0.08]">
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="7d">Last 7 Days</SelectItem>
                  <SelectItem value="30d">Last 30 Days</SelectItem>
                  <SelectItem value="ytd">Year to Date</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                onClick={() => fetchDashboardData()}
                className="border-black/[0.08] rounded-full hover:border-primary hover:text-primary h-9 w-9"
              >
                <ArrowsClockwise className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Mobile Notice */}
          {isMobile && (
            <Card className="p-3 mb-4 bg-amber-50 border-amber-200">
              <div className="flex items-center gap-2">
                <DeviceMobile className="h-4 w-4 text-amber-600" />
                <p className="text-xs text-amber-700">
                  <span className="font-medium">Mobile View:</span> KPIs and view-only access. Use desktop for full admin tools.
                </p>
              </div>
            </Card>
          )}

          {/* Pending Actions Alert */}
          {pendingSubscriptions > 0 && (
            <Card 
              className="p-4 mb-6 bg-amber-50 border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors"
              onClick={() => navigate('/admin/v2/subscriptions?status=pending')}
              data-testid="pending-subscriptions-alert"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-amber-200">
                    <Clock className="h-5 w-5 text-amber-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-amber-800">{pendingSubscriptions} Pending Subscription{pendingSubscriptions !== 1 ? 's' : ''}</p>
                    <p className="text-xs text-amber-700">Review and approve investor subscriptions</p>
                  </div>
                </div>
                <CaretRight className="h-5 w-5 text-amber-600" />
              </div>
            </Card>
          )}

          {/* KPI Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
            <KPICard
              value={kpis?.total_projects?.value}
              label="Total Projects"
              description={kpis?.total_projects?.description}
              icon={Buildings}
              iconColor="text-blue-600"
              bgColor="bg-blue-100"
              onClick={() => handleKPIDrillDown(kpis?.total_projects?.drill_down)}
            />
            <KPICard
              value={kpis?.open_projects?.value}
              label="Open Projects"
              description={kpis?.open_projects?.description}
              icon={TrendUp}
              iconColor="text-green-600"
              bgColor="bg-green-100"
              onClick={() => handleKPIDrillDown(kpis?.open_projects?.drill_down)}
            />
            <KPICard
              value={kpis?.active_investors?.value}
              label="Active Investors"
              description={kpis?.active_investors?.description}
              icon={Users}
              iconColor="text-purple-600"
              bgColor="bg-purple-100"
              onClick={() => handleKPIDrillDown(kpis?.active_investors?.drill_down)}
            />
            {INTERNAL_KYC_UI && (
              <KPICard
                value={kpis?.pending_kyc?.value}
                label="Pending KYC"
                description={kpis?.pending_kyc?.description}
                icon={Warning}
                iconColor="text-amber-600"
                bgColor="bg-amber-100"
                onClick={() => handleKPIDrillDown(kpis?.pending_kyc?.drill_down)}
              />
            )}
            <KPICard
              value={kpis?.total_aum?.value}
              label="Total AUM"
              description={kpis?.total_aum?.description}
              icon={Wallet}
              iconColor="text-emerald-600"
              bgColor="bg-emerald-100"
              format="currency"
              onClick={() => handleKPIDrillDown(kpis?.total_aum?.drill_down)}
            />
            <KPICard
              value={kpis?.total_gdv?.value}
              label="Portfolio GDV"
              description={kpis?.total_gdv?.description}
              icon={ChartBar}
              iconColor="text-cyan-600"
              bgColor="bg-cyan-100"
              format="currency"
              onClick={() => handleKPIDrillDown(kpis?.total_gdv?.drill_down)}
              tooltip={
                <div>
                  <p className="font-medium mb-1">Gross Development Value</p>
                  <p className="text-foreground-muted text-xs">Sum of all project GDVs.</p>
                </div>
              }
            />
            <KPICard
              value={kpis?.total_capex?.value}
              label="Total CAPEX"
              description={kpis?.total_capex?.description}
              icon={Buildings}
              iconColor="text-sky-600"
              bgColor="bg-sky-100"
              format="currency"
              onClick={() => handleKPIDrillDown(kpis?.total_capex?.drill_down)}
              tooltip={
                <div>
                  <p className="font-medium mb-1">Capital deployed into bricks</p>
                  <p className="text-foreground-muted text-xs">
                    Acquisition + Development across all projects: the budget while a project is
                    live, the actual spend once it has exited.
                  </p>
                </div>
              }
            />
            <KPICard
              value={kpis?.total_returns_paid?.value}
              label="Returns Paid"
              description={kpis?.total_returns_paid?.description}
              icon={Coins}
              iconColor="text-pink-600"
              bgColor="bg-pink-100"
              format="currency"
              onClick={() => handleKPIDrillDown(kpis?.total_returns_paid?.drill_down)}
            />
            <KPICard
              value={kpis?.pending_commitments?.value}
              label="Pending Subscriptions"
              description={kpis?.pending_commitments?.description}
              icon={Clock}
              iconColor="text-orange-600"
              bgColor="bg-orange-100"
              onClick={() => navigate('/admin/v2/subscriptions')}
            />
          </div>

          {/* Admin Tools Grid */}
          <div className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-xl font-heading font-semibold mb-4">Admin Tools</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
              
              {/* Core Management - viewable on mobile */}
              <AdminToolCard
                title="Party Management"
                subtitle="Investors & Developers"
                description="Manage investor/developer parties, KYC verification, and accreditation"
                icon={UsersFour}
                iconColor="text-purple-600"
                bgColor="bg-purple-100"
                onClick={() => navigate('/admin/v2/parties')}
                testId="v2-link-parties"
              />
              
              <AdminToolCard
                title="Project Dashboard"
                subtitle="All Projects"
                description="View and manage real estate investment projects"
                icon={Kanban}
                iconColor="text-blue-600"
                bgColor="bg-blue-100"
                onClick={() => navigate('/admin/v2/projects')}
                testId="v2-link-projects"
              />
              
              <AdminToolCard
                title="Subscriptions"
                subtitle="Review & Approve"
                description="Process investor subscriptions and unit allocations"
                icon={Ticket}
                iconColor="text-amber-600"
                bgColor="bg-amber-100"
                onClick={() => navigate('/admin/v2/subscriptions')}
                badge={pendingSubscriptions > 0 ? `${pendingSubscriptions}` : null}
                testId="v2-link-subscriptions"
              />
              
              <AdminToolCard
                title="Instruments"
                subtitle="Securities & Instruments"
                description="Manage instruments (offerings) and cap table positions"
                icon={Coins}
                iconColor="text-indigo-600"
                bgColor="bg-indigo-100"
                onClick={() => navigate('/admin/instruments')}
                testId="v2-link-instruments"
              />
              
              {/* Finance Tools - desktop only for editing */}
              <AdminToolCard
                title="Distributions"
                subtitle="Calculate & Pay"
                description="Calculate distribution entitlements and track payments"
                icon={Calculator}
                iconColor="text-green-600"
                bgColor="bg-green-100"
                onClick={() => navigate('/admin/v2/distributions')}
                testId="v2-link-distributions"
                editOnly={true}
              />
              
              <AdminToolCard
                title="Finance Reports"
                subtitle="Budget vs Actual"
                description="Variance analysis with drill-down to journal entries"
                icon={ChartPie}
                iconColor="text-cyan-600"
                bgColor="bg-cyan-100"
                onClick={() => navigate('/admin/v2/finance')}
                testId="v2-link-finance"
                editOnly={true}
              />
              
              <AdminToolCard
                title="News & Stories"
                subtitle="Content Management"
                description="Write and publish posts for the public news page"
                icon={FileText}
                iconColor="text-slate-600"
                bgColor="bg-slate-100"
                onClick={() => navigate('/admin/news')}
                testId="v2-link-news"
                editOnly={true}
              />
            </div>
          </div>

          {/* Desktop-only footer note */}
          {!isMobile && (
            <div className="text-center text-sm text-foreground-muted">
              <p>All V1 legacy routes have been redirected to their V2 equivalents.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboardPage;
